import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { traderService } from '../services/traderService';
import { toast } from '../components/ui/use-toast';
import { Loader2, Mail, CheckCircle, ArrowLeft, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * ClaimBusiness page - allows users to claim an unclaimed (mockup) business
 * by verifying their email address matches the business email or by creating
 * a new account with email verification.
 */
const ClaimBusiness: React.FC = () => {
  const { traderId } = useParams<{ traderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State passed from TraderDetail (optional, used for pre-filling)
  const _locationState = location.state as { 
    traderId?: string; 
    traderEmail?: string; 
    storeName?: string;
  } | null;
  void _locationState; // Acknowledge unused variable - kept for future use

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [trader, setTrader] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'info' | 'register' | 'verify' | 'success'>('info');
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Load trader data
  useEffect(() => {
    const fetchTrader = async () => {
      if (!traderId) {
        setError('No business ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const traderData = await traderService.getTraderById(traderId);
        
        if (!traderData) {
          setError('Business not found');
          setLoading(false);
          return;
        }

        // Check if business is actually unclaimed (mockup=true, no user_id)
        if (!traderData.mockup || traderData.user_id) {
          setError('This business has already been claimed');
          setLoading(false);
          return;
        }

        // Check if business has a fake email - these cannot be claimed via self-registration
        // They must contact support to claim
        if (traderData.email?.endsWith('@unclaimed.ballymarket.local')) {
          setError('This business cannot be claimed online. Please contact info@ballymarket.ie to claim this listing.');
          setLoading(false);
          return;
        }

        setTrader(traderData);

        // Pre-fill email if available
        if (traderData.email && typeof traderData.email === 'string') {
          setEmail(traderData.email);
        }
      } catch (err) {
        console.error('Error fetching trader:', err);
        setError('Failed to load business details');
      } finally {
        setLoading(false);
      }
    };

    fetchTrader();
  }, [traderId]);

  // Handle claim submission - creates account and sends verification email
  const handleClaimSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!trader) return;

    // Validate form
    if (!email.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter your email address',
        variant: 'destructive',
      });
      return;
    }

    if (!password || password.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive',
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);

      // Create user account with Supabase Auth
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password: password,
        options: {
          data: {
            role: 'trader',
            full_name: trader.owner_name,
            store_name: trader.store_name,
            claiming_business_id: trader.id
          },
          emailRedirectTo: `${window.location.origin}/claim-verify?traderId=${trader.id}`
        }
      });

      if (signUpError) {
        console.error('SignUp error:', signUpError);
        
        if (signUpError.message?.toLowerCase().includes('already registered')) {
          toast({
            title: 'Email Already Registered',
            description: 'This email is already registered. Please log in to claim this business.',
            variant: 'destructive',
          });
          // Redirect to login with return URL
          navigate('/login', { 
            state: { 
              returnTo: `/claim-business/${trader.id}`,
              message: 'Please log in to claim this business'
            } 
          });
          return;
        }
        
        throw signUpError;
      }

      if (!authData.user) {
        throw new Error('Failed to create account');
      }

      // Store claim info for verification callback
      localStorage.setItem('claim_trader_id', trader.id.toString());
      localStorage.setItem('claim_email', email.trim());
      localStorage.setItem('temp_password', password);

      // Move to verification step
      setStep('verify');
      
      toast({
        title: 'Verification Email Sent',
        description: 'Please check your email to verify your account and complete the claim process.',
      });

    } catch (err) {
      console.error('Error claiming business:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to process claim',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading business details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !trader) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Claim</h1>
            <p className="text-gray-600 mb-6">{error || 'Business not found'}</p>
            <Link
              to="/trades"
              className="inline-flex items-center text-blue-600 hover:text-blue-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Traders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Verification sent step
  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h1>
            <p className="text-gray-600 mb-4">
              We've sent a verification email to <strong>{email}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Click the link in the email to verify your account and complete the claim process for <strong>{trader.store_name}</strong>.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left text-sm">
              <p className="text-amber-800">
                <strong>Note:</strong> If you don't see the email, check your spam folder. 
                The verification link will expire in 24 hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success step
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Business Claimed!</h1>
            <p className="text-gray-600 mb-6">
              You now have full access to manage <strong>{trader.store_name}</strong>.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main claim form (info/register step)
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-lg mx-auto px-4">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2">Claim Your Business</h1>
            <p className="text-blue-100">
              Verify your ownership of <strong>{trader.store_name}</strong>
            </p>
          </div>

          {/* Business Info */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-4">
              {trader.avatar_url ? (
                <img
                  src={trader.avatar_url}
                  alt={trader.store_name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div>
                <h2 className="font-semibold text-gray-900">{trader.store_name}</h2>
                <p className="text-sm text-gray-600">{trader.location}</p>
                <p className="text-sm text-gray-500">{trader.owner_name}</p>
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleClaimSubmit} className="p-6 space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="your@email.com"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                We'll send a verification email to confirm your ownership
              </p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Create Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="At least 6 characters"
                minLength={6}
                required
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm your password"
                minLength={6}
                required
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Claim This Business'
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              By claiming this business, you confirm that you are the owner or authorized representative.
            </p>
          </form>

          {/* Already have account */}
          <div className="px-6 pb-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link 
                to="/login" 
                state={{ returnTo: `/claim-business/${trader.id}` }}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimBusiness;
